import React, { useState } from 'react';
import { X, Facebook, Instagram, ChevronRight, ArrowLeft, Menu, Search, ShoppingCart } from 'lucide-react';
import { useCartStore } from '../hooks/useCartStore';
import { Branch } from '../types';
import { SITE_CONFIG } from '../config/siteConfig';
import { AnimatePresence, motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { useResponsiveDevice } from '../hooks/useResponsiveDevice';

interface HeaderProps {
    currentBranch: Branch;
    onBranchChange: (branch: Branch) => void;
    onLogoClick?: () => void;
    onCategorySelect?: (category: string, model?: string) => void;
}

import { deviceService } from '../services/DeviceService';
import { DeviceFamily } from '../types';

const PREVIEWS: Record<string, { image: string, tag: string, spec: string }> = {
    'apple': {
        image: 'https://images.unsplash.com/photo-1616348436168-de43ad0db179?q=80&w=1200&auto=format&fit=crop',
        tag: 'EDICIÓN TITANIO',
        spec: 'DISEÑO ULTRA-DELGADO • FIBRA DE CARBONO REAL'
    },
    'samsung': {
        image: 'https://images.unsplash.com/photo-1610945265064-0e34e5519bbf?q=80&w=1200&auto=format&fit=crop',
        tag: 'FIBRA FORJADA',
        spec: 'TEJIDO DE ALTA RESISTENCIA • AJUSTE ARTESANAL'
    }
};

const Header: React.FC<HeaderProps> = ({ onLogoClick, onCategorySelect }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isSocialMenuOpen, setIsSocialMenuOpen] = useState(false);
    const [activeMegaMenu, setActiveMegaMenu] = useState<'apple' | 'samsung' | null>(null);
    const [selectedSeries, setSelectedSeries] = useState<string | null>(null);
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);

    // Dynamic Data State
    const [appleFamilies, setAppleFamilies] = useState<DeviceFamily[]>([]);
    const [samsungFamilies, setSamsungFamilies] = useState<DeviceFamily[]>([]);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    const { items, setIsDrawerOpen } = useCartStore();
    const cartCount = items.length;
    
    const currentDeviceType = useResponsiveDevice();

    const loadFamilies = async () => {
        const [apple, samsung] = await Promise.all([
            deviceService.getFamiliesByBrand('Apple'),
            deviceService.getFamiliesByBrand('Samsung')
        ]);
        setAppleFamilies(apple);
        setSamsungFamilies(samsung);
    };

    React.useEffect(() => {
        loadFamilies();

        // Realtime Subscription for Families
        const channel = supabase
            .channel('device_families_changes')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'device_families'
                },
                () => {
                    loadFamilies();
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, []);

    const categories = ['Descubrir', 'Catálogo', 'Apple', 'Samsung', 'Accesorios'];

    const closeMegaMenu = () => {
        setActiveMegaMenu(null);
        setSelectedSeries(null);
        setIsSocialMenuOpen(false);
    };

    const handleCategoryClick = (category: string) => {
        // Normalize for logical keys (lowercase + remove accents)
        const cat = category.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        
        setIsSocialMenuOpen(false);
        if (cat === 'apple' || cat === 'samsung') {
            setActiveMegaMenu(activeMegaMenu === cat ? null : cat as 'apple' | 'samsung');
            setSelectedSeries(null); // Reset selection when opening
        } else {
            setActiveMegaMenu(null);
            onCategorySelect?.(cat);
        }
    };

    const currentModels = activeMegaMenu === 'apple' ? appleFamilies : samsungFamilies;
    const activeSeriesData = currentModels.find(m => m.id === selectedSeries);
    const hoveredFamily = currentModels.find(m => m.name === hoveredItem);

    // Determine Preview Image Logic
    const getPreviewImage = () => {
        if (!activeMegaMenu) return '';

        // 1. Hovered Family Image
        if (hoveredItem) {
            const family = currentModels.find(m => m.name === hoveredItem);
            if (family?.image_url) return family.image_url;
        }

        // 2. Selected Series Image
        if (selectedSeries) {
            if (activeSeriesData?.image_url) return activeSeriesData.image_url;
        }

        // 3. Fallback to Brand Default
        return PREVIEWS[activeMegaMenu].image;
    };

    const currentPreviewImage = getPreviewImage();

    return (
        <>
            {/* Context Backdrop - Captures clicks outside the header/menu */}
            <AnimatePresence>
                {activeMegaMenu && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeMegaMenu}
                        className="fixed inset-0 z-[48] bg-black/40 backdrop-blur-sm cursor-pointer"
                    />
                )}
            </AnimatePresence>

            <header className="sticky top-0 z-50 bg-zinc-950/40 backdrop-blur-2xl border-b border-white/5 supports-[backdrop-filter]:bg-zinc-950/20">
                <div className="max-w-[1800px] mx-auto px-6 md:px-12">
                    <div className="flex justify-between items-center h-16 md:h-20">
                        {/* Tesla Layout: Left - Logo */}
                        <div className="flex-1 flex justify-start">
                            <div
                                id="header-logo"
                                onClick={() => {
                                    closeMegaMenu();
                                    onLogoClick?.();
                                    window.location.href = '/'; // Force reload to home
                                }}
                                className="cursor-pointer transition-all active:scale-95 py-2 group"
                            >
                                <img
                                    src={SITE_CONFIG.logo}
                                    alt={SITE_CONFIG.brandName}
                                    className="h-8 md:h-10 w-auto object-contain transition-all duration-300 drop-shadow-[0_0_0_rgba(255,255,255,0)] group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.5)] group-active:drop-shadow-[0_0_12px_rgba(255,255,255,0.8)]"
                                />
                            </div>
                        </div>

                        {/* Tesla Layout: Center - Navigation Categories */}
                        <nav className="hidden lg:flex items-center gap-2">
                            {categories.map((category) => (
                                <button
                                    key={category}
                                    id={`nav-category-${category.toLowerCase()}`}
                                    onClick={() => handleCategoryClick(category)}
                                    className={`px-4 py-2 text-sm font-bold uppercase transition-all tracking-wide rounded-md ${activeMegaMenu === category.toLowerCase()
                                        ? 'text-white bg-white/5'
                                        : 'text-white hover:bg-zinc-800/50'
                                        }`}
                                >
                                    {category}
                                </button>
                            ))}

                            {/* Social Media Dropdown Integrated in Nav */}
                            <div
                                className="relative py-2"
                                onMouseLeave={() => setIsSocialMenuOpen(false)}
                            >
                                <button
                                    id="btn-social-nav"
                                    onClick={() => onCategorySelect?.('social')}
                                    className="px-4 py-2 text-sm font-bold uppercase text-white hover:bg-zinc-800/50 rounded-md transition-all flex items-center gap-2"
                                >
                                    Social
                                </button>

                            </div>
                        </nav>

                        {/* Tesla Layout: Right - Actions & Mobile Menu */}
                        <div className="flex-1 flex justify-end items-center gap-2 md:gap-4">
                            {/* Search Button */}
                            <button
                                id="btn-search-header"
                                onClick={() => setIsSearchOpen(!isSearchOpen)}
                                className="p-2 text-white hover:bg-zinc-800/50 rounded-full transition-all group"
                                aria-label="Buscar"
                            >
                                <Search className="w-5 h-5 md:w-6 md:h-6 group-hover:scale-110 transition-transform" />
                            </button>

                            {/* Cart Button */}
                            <button
                                id="btn-cart-header"
                                onClick={() => setIsDrawerOpen(true)}
                                className="p-2 text-white hover:bg-zinc-800/50 rounded-full transition-all group relative"
                                aria-label="Carrito"
                            >
                                <ShoppingCart className="w-5 h-5 md:w-6 md:h-6 group-hover:scale-110 transition-transform" />
                                {cartCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-[#1aaaff] text-black text-[10px] font-black w-4 h-4 rounded-full flex items-center justify-center animate-bounce shadow-[0_0_10px_rgba(26,170,255,0.5)]">
                                        {cartCount}
                                    </span>
                                )}
                            </button>

                            {/* Mobile Menu Toggle */}
                            <button
                                id="btn-mobile-menu"
                                onClick={() => setIsMobileMenuOpen(true)}
                                className="lg:hidden p-2 text-white hover:bg-zinc-800/50 rounded-full transition-all"
                            >
                                <Menu className="w-6 h-6" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Search Overlay */}
                <AnimatePresence>
                    {isSearchOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="bg-zinc-950 border-b border-white/5 overflow-hidden shadow-2xl"
                        >
                            <div className="max-w-[1800px] mx-auto px-6 py-8 md:py-12">
                                <div className="relative max-w-4xl mx-auto">
                                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-zinc-500 w-5 h-5" />
                                    <input 
                                        autoFocus
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="¿Qué estás buscando?"
                                        className="w-full bg-zinc-900 border border-white/5 py-5 pl-16 pr-20 text-lg md:text-xl font-medium text-white outline-none focus:border-[#1aaaff]/50 transition-all rounded-xl"
                                    />
                                    <button 
                                        onClick={() => setIsSearchOpen(false)}
                                        className="absolute right-6 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                                <div className="mt-6 flex flex-wrap justify-center gap-3">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-zinc-600 w-full text-center mb-2">Búsquedas populares</span>
                                    {['Forged Carbon', 'iPhone 15 Pro', 'Alcantara', 'S24 Ultra'].map((tag) => (
                                        <button 
                                            key={tag}
                                            onClick={() => setSearchQuery(tag)}
                                            className="px-4 py-2 bg-zinc-900 border border-white/5 rounded-full text-[10px] font-bold text-zinc-400 hover:text-white hover:border-white/20 transition-all uppercase tracking-wider"
                                        >
                                            {tag}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Drill-down Mega Menu */}
                <AnimatePresence>
                    {activeMegaMenu && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="absolute top-full left-0 w-full bg-zinc-950/95 backdrop-blur-3xl border-b border-white/5 overflow-hidden shadow-2xl z-[50]"
                        >
                            {/* Floating Close Button for Mobile UX */}
                            <button
                                onClick={closeMegaMenu}
                                className="absolute top-6 right-6 p-2 text-zinc-500 hover:text-white transition-colors lg:hidden"
                                aria-label="Cerrar menú"
                            >
                                <X className="w-6 h-6" />
                            </button>

                            <div className="max-w-[1800px] mx-auto px-6 py-12 md:py-16">
                                <div className="flex flex-col lg:flex-row gap-16">
                                    {/* Left Side: Navigation */}
                                    <div className="flex-grow">
                                        <AnimatePresence mode="wait">
                                            {!selectedSeries ? (
                                                <div className="flex flex-col gap-8">
                                                    <motion.div
                                                        key="series"
                                                        initial={{ x: -20, opacity: 0 }}
                                                        animate={{ x: 0, opacity: 1 }}
                                                        exit={{ x: -20, opacity: 0 }}
                                                        className="grid grid-cols-2 md:grid-cols-4 gap-4"
                                                    >
                                                        {currentModels.map((item) => (
                                                            <button
                                                                key={item.id}
                                                                onClick={() => {
                                                                    setSelectedSeries(item.id);
                                                                    setHoveredItem(null);
                                                                }}
                                                                onMouseEnter={() => setHoveredItem(item.name)}
                                                                className="flex flex-col items-start gap-4 p-6 rounded-none hover:bg-white/[0.03] transition-all group border border-transparent text-left"
                                                            >
                                                                <div className="flex items-center justify-between w-full">
                                                                    <span className="text-sm font-black uppercase tracking-widest text-zinc-500 group-hover:text-white transition-colors">
                                                                        {item.name.replace(' Series', '')}
                                                                    </span>
                                                                    <ChevronRight className="w-4 h-4 text-zinc-800 group-hover:text-white group-hover:translate-x-1 transition-all" />
                                                                </div>
                                                                <div className="h-px w-0 group-hover:w-8 bg-white transition-all duration-300" />
                                                            </button>
                                                        ))}
                                                    </motion.div>

                                                    {/* Bottom Exit Action for Mobile UX */}
                                                    <button
                                                        onClick={closeMegaMenu}
                                                        className="lg:hidden flex items-center justify-center gap-2 py-4 border-t border-white/5 text-[10px] font-black tracking-[0.3em] text-zinc-500 hover:text-white transition-colors"
                                                    >
                                                        <X className="w-3 h-3" /> CERRAR MENÚ
                                                    </button>
                                                </div>
                                            ) : (
                                                <motion.div
                                                    key="variants"
                                                    initial={{ x: 20, opacity: 0 }}
                                                    animate={{ x: 0, opacity: 1 }}
                                                    exit={{ x: 20, opacity: 0 }}
                                                    className="flex flex-col"
                                                >
                                                    <div className="flex justify-between items-center mb-8 px-6">
                                                        <button
                                                            onClick={() => setSelectedSeries(null)}
                                                            className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors group"
                                                        >
                                                            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                                            <span className="text-xs font-black uppercase tracking-widest">Regresar</span>
                                                        </button>

                                                        <button onClick={closeMegaMenu} className="lg:hidden text-zinc-500 hover:text-white">
                                                            <X className="w-5 h-5" />
                                                        </button>
                                                    </div>

                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                        {activeSeriesData?.variants?.map((variant) => (
                                                            <button
                                                                key={variant}
                                                                onClick={() => {
                                                                    onCategorySelect?.(activeMegaMenu!, variant);
                                                                    closeMegaMenu();
                                                                }}
                                                                onMouseEnter={() => setHoveredItem(variant)}
                                                                className="p-6 text-left border border-white/5 hover:border-white/30 hover:bg-white/5 transition-all group relative overflow-hidden"
                                                            >
                                                                <h4 className="text-sm font-black uppercase tracking-widest text-white group-hover:text-white transition-colors">{variant}</h4>
                                                                <div className="mt-2 text-[8px] font-bold tracking-[0.2em] text-zinc-600 uppercase group-hover:text-zinc-400">Ver Detalles</div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* Right Side: Visual Preview (SpaceX/Tesla Aesthetic) */}
                                    <div className="hidden lg:flex flex-col w-[35%] shrink-0 border-l border-white/5 pl-16">
                                        <div className="sticky top-0 h-full flex flex-col justify-center">
                                            <AnimatePresence mode="wait">
                                                <motion.div
                                                    key={hoveredItem || activeMegaMenu}
                                                    initial={{ opacity: 0, scale: 1.02, filter: 'blur(10px)' }}
                                                    animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                                                    exit={{ opacity: 0, scale: 0.98, filter: 'blur(10px)' }}
                                                    transition={{ duration: 0.4, ease: "easeOut" }}
                                                    className="relative aspect-[4/5] bg-zinc-900 overflow-hidden rounded-2xl border border-white/5 shadow-2xl"
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10" />
                                                    <img
                                                        src={currentPreviewImage}
                                                        alt="Preview"
                                                        className="absolute inset-0 w-full h-full object-cover opacity-60 grayscale hover:grayscale-0 transition-all duration-700 pointer-events-none select-none"
                                                        style={{
                                                            transform: `scale(${(hoveredFamily || activeSeriesData)?.framing_settings?.[currentDeviceType]?.scale || 1})`,
                                                            objectPosition: `${(hoveredFamily || activeSeriesData)?.framing_settings?.[currentDeviceType]?.x ?? 50}% ${(hoveredFamily || activeSeriesData)?.framing_settings?.[currentDeviceType]?.y ?? 50}%`
                                                        }}
                                                    />

                                                    {/* Clean Info Underlays */}
                                                    <div className="absolute inset-0 z-20 p-10 flex flex-col justify-end pointer-events-none">
                                                        <div className="space-y-4">
                                                            <motion.div
                                                                initial={{ y: 5, opacity: 0 }}
                                                                animate={{ y: 0, opacity: 1 }}
                                                                transition={{ delay: 0.1 }}
                                                                className="space-y-1"
                                                            >
                                                                <span className="text-[9px] font-black tracking-[0.4em] text-zinc-500 uppercase mb-2 block">Línea Exclusiva</span>
                                                                <h3 className="text-4xl font-black uppercase tracking-tighter text-white leading-none">
                                                                    {hoveredItem || activeSeriesData?.name.replace(' Series', '') || activeMegaMenu}
                                                                </h3>
                                                            </motion.div>
                                                            <motion.div
                                                                initial={{ y: 5, opacity: 0 }}
                                                                animate={{ y: 0, opacity: 1 }}
                                                                transition={{ delay: 0.15 }}
                                                                className="flex flex-col gap-1"
                                                            >
                                                                <p className="text-xs font-bold text-white tracking-widest uppercase">
                                                                    {PREVIEWS[activeMegaMenu!].tag}
                                                                </p>
                                                                <p className="text-[9px] font-medium text-zinc-500 max-w-[250px] leading-relaxed uppercase tracking-[0.2em]">
                                                                    {PREVIEWS[activeMegaMenu!].spec}
                                                                </p>
                                                            </motion.div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            </AnimatePresence>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </header>

            {/* Mobile Navigation Drawer */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed inset-0 z-[60] bg-black flex flex-col"
                    >
                        <div className="flex justify-end p-8">
                            <button onClick={() => setIsMobileMenuOpen(false)} className="text-white hover:scale-110 transition-transform">
                                <X className="h-8 w-8" />
                            </button>
                        </div>

                        <nav className="flex flex-col px-12 gap-8 text-left py-10 overflow-y-auto">
                            {categories.map((cat) => (
                                <button
                                    key={cat}
                                    onClick={() => {
                                        const logicalCat = cat.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                                        if (logicalCat === 'apple' || logicalCat === 'samsung') {
                                            setActiveMegaMenu(logicalCat as 'apple' | 'samsung');
                                            setIsMobileMenuOpen(false);
                                        } else {
                                            onCategorySelect?.(logicalCat);
                                            setIsMobileMenuOpen(false);
                                        }
                                    }}
                                    className="text-2xl font-bold text-white uppercase tracking-widest hover:pl-4 transition-all text-left"
                                >
                                    {cat}
                                </button>
                            ))}

                            <hr className="border-zinc-800 my-4" />

                            <div className="flex flex-col gap-6">
                                <button 
                                    onClick={() => {
                                        onCategorySelect?.('social');
                                        setIsMobileMenuOpen(false);
                                    }}
                                    className="text-2xl font-bold text-[#1aaaff] uppercase tracking-widest text-left"
                                >
                                    Social Feed
                                </button>
                                <div className="h-px bg-zinc-900 w-12" />
                                <a href={SITE_CONFIG.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="text-xl text-white font-bold hover:text-zinc-300 transition-colors">Instagram</a>
                                <a href={SITE_CONFIG.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="text-xl text-white font-bold hover:text-zinc-300 transition-colors">Facebook</a>
                                <a href={SITE_CONFIG.socialMedia.tiktok} target="_blank" rel="noopener noreferrer" className="text-xl text-white font-bold hover:text-zinc-300 transition-colors">TikTok</a>
                            </div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Header;
